import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './AddUsers.module.scss';
import Input from 'component/atom/forms/input/input/Input';
import Select from 'component/atom/forms/select/select/Select';
import Button from 'component/atom/forms/button/Button';
import { usePost } from 'hooks/usePost';
import { useFetch } from 'hooks/useFetch';
import { groupEndpoints, roleEndpoints } from 'hooks/endpoints';

const AddUsers = forwardRef(
  ({ onClose, onSuccess, groupId, refetch, setRefetch }, ref) => {
    const { mutate: AddUser, isPending } = usePost();

    const { data: roles, isFetching: isFetchingRoles } = useFetch(
      ['getRoles'],
      roleEndpoints?.getRoles(),
    );

    const rolesOptions = useMemo(
      () =>
        roles?.data?.map((val) => ({
          value: val.roleId,
          label: val.roleName,
        })),
      [roles],
    );

    const validationSchema = Yup.object().shape({
      firstName: Yup.string().trim().required('First Name is required'),
      lastName: Yup.string().trim().required('Last Name is required'),
      displayEmail: Yup.string()
        .trim()
        .email('Please enter a valid email')
        .required('Email is required'),
      username: Yup.string().trim().required('Username is required'),
      password: Yup.string().required('Password is required'),
      role: Yup.string().required('Role is required'),
    });

    const formik = useFormik({
      initialValues: {
        firstName: '',
        lastName: '',
        displayEmail: '',
        department: '',
        username: '',
        mobile: '',
        phone: '',
        password: '',
        role: '',
        userId: null,
        userGroupId: groupId,
      },
      validationSchema,
      onSubmit: (values, helpers) => {
        AddUser(
          { data: values, url: groupEndpoints.createUserGroupMember() },
          {
            onSuccess: (e) => {
              if (!e?.data?.isSuccess) return;
              setRefetch(!refetch);
              helpers.resetForm();
              onSuccess?.();
            },
          },
        );
      },
    });

    useImperativeHandle(ref, () => ({
      handleSubmit: formik.handleSubmit,
    }));

    return (
      <form className={styles['add-user-form']} onSubmit={formik.handleSubmit}>
        <Input
          label='First Name'
          placeholder='Enter First Name'
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='firstName'
          error={formik.errors.firstName}
          touched={formik.touched.firstName}
        />

        <Input
          label='Last Name'
          placeholder='Enter Last Name'
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='lastName'
          error={formik.errors.lastName}
          touched={formik.errors.lastName}
        />

        <Input
          label='Email Address'
          placeholder='Enter Email Address'
          value={formik.values.displayEmail}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='displayEmail'
          error={formik.errors.displayEmail}
          touched={formik.errors.displayEmail}
        />

        <Input
          label='Department'
          placeholder='Enter Department'
          value={formik.values.department}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='department'
          error={formik.errors.department}
          touched={formik.touched.department}
        />

        <Input
          label='Username'
          placeholder='Enter Username'
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='username'
          error={formik.errors.username}
          touched={formik.touched.username}
        />

        <Input
          label='Mobile'
          placeholder='Enter Mobile'
          value={formik.values.mobile}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='mobile'
          error={formik.errors.mobile}
          touched={formik.touched.mobile}
        />

        <Input
          label='Phone'
          placeholder='Enter Phone'
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='phone'
          error={formik.errors.phone}
          touched={formik.touched.phone}
        />

        <Input
          label='Password'
          placeholder='Create Password'
          inputType='password'
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='password'
          error={formik.errors.password}
          touched={formik.touched.password}
        />

        <Select
          label='Role'
          placeholder='Select Role'
          options={rolesOptions}
          value={formik.values.role}
          onChange={formik.handleChange}
          name='role'
          error={formik.errors.role}
          touched={formik.touched.role}
          isLoading={isFetchingRoles}
          onBlur={formik.handleBlur}
        />

        <div className={styles['actions-container']}>
          <Button
            label='Cancel'
            variant='dark'
            onClick={(e) => {
              e?.preventDefault?.();
              onClose?.();
            }}
          />
          <Button
            label='Save'
            type='save'
            onClick={formik.handleSubmit}
            disabled={isPending}
          />
        </div>
      </form>
    );
  },
);

export default AddUsers;
